import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';

/** One column of an exported sheet. `key` indexes into each row object. */
export interface ExcelExportColumn {
  header: string;
  key: string;
  /** Column width in Excel character units. */
  width?: number;
}

/**
 * A range to merge, addressed in **data coordinates**: row 0 is the first data
 * row and column 0 the first column, both ignoring the header row. Keeping call
 * sites in data coordinates means they never have to know how many rows precede
 * the data — the classic off-by-one when the sheet's preamble changes.
 */
export interface ExcelExportMerge {
  startRow: number;
  endRow: number;
  startCol: number;
  endCol: number;
}

export interface ExcelExportOptions {
  /** File name, without the .xlsx extension. */
  fileName: string;
  sheetName: string;
  columns: ExcelExportColumn[];
  rows: any[];
  merges?: ExcelExportMerge[];
  /** Mirror the sheet for Arabic. */
  rightToLeft?: boolean;
}

/** Sheet row (1-based) of the first data row: row 1 holds the headers. */
const FIRST_DATA_ROW = 2;

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Builds and downloads a single-sheet .xlsx: a styled header row followed by the
 * data. Rather than repeat the workbook setup at every call site, list your
 * columns and rows and let this handle it.
 *
 * ExcelJS is imported dynamically to keep it out of the initial bundle.
 */
@Injectable({
  providedIn: 'root',
})
export class ExcelExportService {
  async downloadSheet(options: ExcelExportOptions): Promise<void> {
    const excelJsModule: any = await import('exceljs');
    const ExcelJS = excelJsModule.default ?? excelJsModule;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(options.sheetName, {
      views: [{ rightToLeft: !!options.rightToLeft }],
    });

    // Declaring columns without a header lets us address cells by key while
    // writing the header row ourselves.
    worksheet.columns = options.columns.map(column => ({
      key: column.key,
      width: column.width,
    }));

    const headerRow = worksheet.getRow(1);
    options.columns.forEach((column, index) => {
      const cell = headerRow.getCell(index + 1);
      cell.value = column.header;
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF231F20' },
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });
    headerRow.commit();

    options.rows.forEach((row, rowIndex) => {
      const sheetRow = worksheet.getRow(rowIndex + FIRST_DATA_ROW);
      options.columns.forEach((column, columnIndex) => {
        const cell = sheetRow.getCell(columnIndex + 1);
        cell.value = row[column.key] ?? '';
        // Cells routinely hold newline-separated bullet lists. Without wrapText
        // Excel shows only the text up to the first newline and hides the rest
        // until the cell is opened for editing. Top alignment keeps a value at
        // the top of its merged block rather than floating to the bottom.
        cell.alignment = { vertical: 'top', wrapText: true };
      });
      sheetRow.commit();
    });

    (options.merges ?? []).forEach(merge => {
      worksheet.mergeCells(
        merge.startRow + FIRST_DATA_ROW,
        merge.startCol + 1,
        merge.endRow + FIRST_DATA_ROW,
        merge.endCol + 1
      );
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: XLSX_MIME }), `${options.fileName}.xlsx`);
  }
}
